// src/components/NavBar/NavBar.js
import React, { useState, useEffect, useRef } from 'react';
import { FiMenu, FiX } from 'react-icons/fi';
import './NavBar.css';
import logo_image from '../Assets/logo-image-main.png';

const MOBILE_BREAKPOINT = 900; // px – keep in sync with CSS

const NavBar = ({
  logo = 'Man of Valor',
  links = [
    { label: 'Home',               href: '#home'    },
    { label: 'About Us',           href: '#about'   },
    { label: 'Events',             href: '#events'  },
    { label: 'Support',            href: '#support' },
  ],
}) => {
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [scrolled,  setScrolled]  = useState(false);
  const [expanded,  setExpanded]  = useState(false);
  const [tilt,      setTilt]      = useState({ x: 0, y: 0 });
  const [hovered,   setHovered]   = useState(false);
  const [isMobile,  setIsMobile]  = useState(false);

  const lastY = useRef(0);
  const navRef = useRef(null);

  // Detect mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);

      // Reset state when going back to desktop
      if (!mobile) {
        setMenuOpen(false);
        setExpanded(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen, isMobile]);

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const goingDown = y > lastY.current;

      // Mark as "scrolled" after half a viewport height
      setScrolled(y > window.innerHeight * 0.5);

      // Only collapse mini-bar on desktop
      if (!isMobile && goingDown) {
        setExpanded(false);
      }

      // On mobile, close menu when scrolling
      if (isMobile && menuOpen && Math.abs(y - lastY.current) > 10) {
        setMenuOpen(false);
      }

      lastY.current = y;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, menuOpen]);

  // Close menu when clicking outside on mobile
  useEffect(() => {
    if (!isMobile || !menuOpen) return;

    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobile, menuOpen]);

  const handleMiniEnter = () => {
    if (!isMobile) setExpanded(true);
  };

  const handleMiniClick = () => {
    if (!isMobile) {
      setExpanded(true);
    } else {
      setMenuOpen(true);
    }
  };

  const handleMouseMove = (e) => {
    if (isMobile) return;
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - left - width / 2;
    const y = e.clientY - top - height / 2;
    setTilt({ x: (y / (height / 2)) * 5, y: -(x / (width / 2)) * 5 });
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    setTilt({ x: 0, y: 0 });
    setHovered(false);
  };

  const handleMouseEnter = () => {
    if (!isMobile) setHovered(true);
  };

  const handleLinkClick = (e, href) => {
    // Prevent default anchor behavior
    e.preventDefault();
    
    // Close mobile menu if open
    if (isMobile) {
      setMenuOpen(false);
    }
    
    // Smooth scroll to section
    if (href && href.startsWith('#')) {
      const targetId = href.substring(1);
      const targetElement = document.getElementById(targetId);
      
      if (targetElement) {
        const offset = 100; // Account for navbar height
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  // Mini collapsed state: desktop only
  const collapsed = !isMobile && scrolled && !expanded;

  return (
    <>
      <nav
        ref={navRef}
        className={
          `navbar ${isMobile ? 'navbar--mobile' : ''}` +
          (scrolled   ? ' navbar--solid'      : '') +
          (hovered    ? ' navbar--hovered'    : '') +
          (collapsed  ? ' navbar--collapsed'  : '') +
          (menuOpen   ? ' navbar--menu-open'  : '')
        }
        style={{
          '--rotateX': `${tilt.x}deg`,
          '--rotateY': `${tilt.y}deg`,
        }}
        onMouseMove={collapsed || isMobile ? undefined : handleMouseMove}
        onMouseLeave={collapsed || isMobile ? undefined : handleMouseLeave}
        onMouseEnter={collapsed || isMobile ? undefined : handleMouseEnter}
      >
        {/* full navbar */}
        <div className="navbar__inner">
          <a
            href="#home"
            className="navbar__logo"
            onClick={(e) => handleLinkClick(e, '#home')}
          >
            {isMobile ? (
              <img src={logo_image} alt={logo} className="navbar__logo-img" />
            ) : (
              logo
            )}
          </a>

          <ul className="navbar__menu">
            {links.map((lnk, i) => (
              <li key={lnk.href || i}>
                <a
                  href={lnk.href}
                  className="navbar__link"
                  onClick={(e) => handleLinkClick(e, lnk.href)}
                >
                  <span>{lnk.label}</span>
                </a>
              </li>
            ))}
          </ul>

          <button
            className="navbar__hamburger"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            type="button"
          >
            {menuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>

        {/* mini logo – desktop only */}
        {!isMobile && collapsed && (
          <button
            className="navbar__mini-logo"
            onClick={handleMiniClick}
            onMouseEnter={handleMiniEnter}
            aria-label="Expand menu"
            type="button"
          >
            <img src={logo_image} alt={logo} className="logo_expanded" />
          </button>
        )}
      </nav>

      {/* mobile overlay */}
      <div
        className={`navbar__mobile-overlay ${menuOpen && isMobile ? 'open' : ''}`}
        onClick={(e) => {
          // Close menu when clicking backdrop
          if (e.target === e.currentTarget) {
            setMenuOpen(false);
          }
        }}
        aria-hidden={!menuOpen || !isMobile}
      >
        <div className="navbar__mobile-content">
          <ul className="navbar__mobile-list">
            {links.map((lnk, i) => (
              <li key={lnk.href || i}>
                <a
                  href={lnk.href}
                  className="navbar__mobile-link"
                  onClick={(e) => handleLinkClick(e, lnk.href)}
                >
                  {lnk.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

export default NavBar;
